import { ATTRIBUTE_SOURCE_RANK } from '../constants/markerAttributes.js';
function isSlot(x) {
    if (!x || typeof x !== 'object')
        return false;
    const s = x;
    return (s.value !== undefined &&
        s.value !== null &&
        typeof s.source === 'string' &&
        Object.prototype.hasOwnProperty.call(ATTRIBUTE_SOURCE_RANK, s.source));
}
// Explicit per-path reads (no dynamic property walk): type-checked against MarkerAttributes
// and free of prototype-key indexing.
function readLeaf(a, path) {
    switch (path) {
        case 'price.level': return a.price?.level;
        case 'price.from': return a.price?.from;
        case 'openingPeriods': return a.openingPeriods;
        case 'age.min': return a.age?.min;
        case 'age.max': return a.age?.max;
        case 'accessibility.stepFree': return a.accessibility?.stepFree;
        case 'accessibility.wheelchair': return a.accessibility?.wheelchair;
        case 'accessibility.accessibleToilet': return a.accessibility?.accessibleToilet;
        case 'accessibility.seating': return a.accessibility?.seating;
        case 'accessibility.lift': return a.accessibility?.lift;
        case 'accessibility.walkingEffort': return a.accessibility?.walkingEffort;
        case 'indoorOutdoor': return a.indoorOutdoor;
        case 'typicalDurationMin': return a.typicalDurationMin;
        case 'booking': return a.booking;
        case 'petsAllowed': return a.petsAllowed;
        case 'contact.website': return a.contact?.website;
        case 'contact.phone': return a.contact?.phone;
        case 'dining.cuisine': return a.dining?.cuisine;
        case 'dining.dietary': return a.dining?.dietary;
        case 'dining.meals': return a.dining?.meals;
        case 'arts.guidedTours': return a.arts?.guidedTours;
        case 'arts.audioGuide': return a.arts?.audioGuide;
        case 'nature.terrain': return a.nature?.terrain;
        case 'shopping.productCategories': return a.shopping?.productCategories;
        case 'events.start': return a.events?.start;
        case 'events.end': return a.events?.end;
        case 'events.recurrence': return a.events?.recurrence;
        case 'events.ticketRequired': return a.events?.ticketRequired;
        default: return undefined;
    }
}
/** The slot at a path; undefined for any missing level or malformed slot. */
export function getAttribute(attributes, path) {
    if (!attributes || typeof attributes !== 'object')
        return undefined;
    let leaf;
    try {
        leaf = readLeaf(attributes, path);
    }
    catch {
        return undefined;
    }
    return isSlot(leaf) ? leaf : undefined;
}
function time(setAt) {
    const t = setAt instanceof Date ? setAt.getTime() : new Date(setAt).getTime();
    return Number.isNaN(t) ? -Infinity : t;
}
/** Positive when `a` beats `b`: rank first, then newer setAt. */
export function compareAttributeValues(a, b) {
    const byRank = ATTRIBUTE_SOURCE_RANK[a.source] - ATTRIBUTE_SOURCE_RANK[b.source];
    if (byRank !== 0)
        return byRank;
    const ta = time(a.setAt);
    const tb = time(b.setAt);
    if (ta === tb)
        return 0;
    return ta > tb ? 1 : -1;
}
/**
 * Winning slot among the stored one and caller-supplied fallbacks (e.g. category
 * defaults). A full tie keeps the stored slot. Never throws on malformed input.
 */
export function resolveAttribute(marker, path, fallbacks = []) {
    let best;
    try {
        best = getAttribute(marker?.attributes, path);
    }
    catch {
        best = undefined;
    }
    for (const candidate of fallbacks) {
        if (!isSlot(candidate))
            continue;
        if (!best || compareAttributeValues(candidate, best) > 0)
            best = candidate;
    }
    return best;
}
/** true | false | 'unknown'. Absence and non-boolean values are unknown, never false. */
export function resolveTriState(marker, path) {
    const slot = resolveAttribute(marker, path);
    return slot && typeof slot.value === 'boolean' ? slot.value : 'unknown';
}
/** The write-side rule every attribute writer must call before replacing a slot. */
export function shouldOverwriteAttribute(existing, incomingSource) {
    if (!isSlot(existing))
        return true;
    if (incomingSource === 'editor')
        return true;
    return ATTRIBUTE_SOURCE_RANK[incomingSource] >= ATTRIBUTE_SOURCE_RANK[existing.source];
}
//# sourceMappingURL=markerAttributes.js.map